import { AxiosError, AxiosResponse } from 'axios';
import { LARVA, LARVA_DOOR_ACCESS } from 'core/datasets/action';
import { coreActions } from 'core/redux/actions/coreActions';
import { AppState } from 'core/redux/rootReducer';
import { handleErrorResponse } from 'core/saga/workers/handleErrorResponse';
import { apply, call, put, select } from 'redux-saga/effects';
import { larvaDoorAccessActions } from 'smartHome/larva/redux/actions/larvaDoorAccessActions';
import { LarvaApiUnits } from 'smartHome/larva/types/larvaData';
import { UserData } from 'tenantUser/types/user';
import { fetchLarvaApiUnits } from '../../utils/fetchUnitDevicesMap';
import { generateDoorAccessMapData } from 'smartHome/larva/helpers/larvaHelpers';
import DoorCardRawData from 'smartHome/larva/classes/DoorCardRawData';
import DoorCodeRawData from 'smartHome/larva/classes/DoorCodeRawData';
import LarvaConnectionConfig from 'smartHome/larva/classes/LarvaConnectionConfig';
import LarvaDoorAccessController from 'smartHome/larva/classes/LarvaDoorAccessController';
import LarvaWebClient from 'smartHome/larva/classes/LarvaWebClient';
import IDoorCardRawData from 'smartHome/larva/types/doorAccess/IDoorCardRawData';
import IDoorCodeRawData from 'smartHome/larva/types/doorAccess/IDoorCodeRawData';
import LarvaConnectionData, { LarvaConnectionDataWithoutToken } from '../../types/LarvaConnectionData';
import IListViewData from 'core/types/IListViewData';
import ILarvaDoorAccess from 'smartHome/larva/types/doorAccess/ILarvaDoorAccess';
import { agreementObjectsSelectors } from '../../../../agreementObject/redux/agreementObjectsReducer';
import TokenService from 'core/config/gql/TokenService';

export const isCurrentUserTenantSelector = (state: AppState): boolean => {
    const selectedAgreement = state.agreements.selectedAgreement;
    const tenants = selectedAgreement?.tenants as UserData[];
    const user = state.auth.user as UserData;

    if (!tenants || !user || !user.email) {
        return false;
    }

    return tenants.some((tenant) => user.email === tenant.email);
};

export const objectHasLarvaSupportSelector = (state: AppState): boolean => {
    const selectedObject = state.agreementObjects.selectedObject;
    return !!(
        selectedObject &&
        selectedObject.smartHome &&
        selectedObject.smartHome.isAvailable &&
        selectedObject.smartHome.type === 'larva'
    );
};

export const isAllowedToManageSelector = (state: AppState): boolean => {
    return state.larva.doorAccess.isAllowedToManage;
};

const tokenService = TokenService.getInstance();

// This function prepares connection configuration and emits events to fetch units/devices and door access
export function* fetchAllLarvaData(): Generator {
    yield put({ type: LARVA.API_UNITS_FETCH_STARTED });
    yield put({ type: LARVA.API_DOOR_ACCESS_FETCH_STARTED });
}

export function* fetchApiUnits(): Generator {
    const supportsLarva = (yield select(objectHasLarvaSupportSelector)) as boolean;
    if (!supportsLarva) {
        return;
    }

    const connectionData = (yield select(
        //@ts-ignore
        agreementObjectsSelectors.smartHomeSettingsSelector,
    )) as LarvaConnectionDataWithoutToken | null;

    if (!connectionData) {
        return;
    }

    yield call([tokenService, tokenService.refreshToken]);
    const token = yield call([tokenService, tokenService.getToken]);

    try {
        const result = (yield call(
            fetchLarvaApiUnits,
            new LarvaConnectionConfig({ ...connectionData, token: String(token) }),
        )) as LarvaApiUnits;

        yield put({
            type: LARVA.PUT_API_UNITS,
            payload: result,
        });
    } catch (error) {
        yield put({
            type: LARVA.API_UNITS_REQUEST_FAILED,
            payload: error,
        });
    }
}

export function* changeDoorAccessRights(): Generator {
    const isCurrentUserTenant = yield select(isCurrentUserTenantSelector);
    yield put({
        type: LARVA_DOOR_ACCESS.CHANGED_RIGHT_TO_MANAGE_ACCESSES,
        payload: isCurrentUserTenant,
    });
}

export function* fetchDoorAccess(): Generator {
    const supportsLarva = (yield select(objectHasLarvaSupportSelector)) as boolean;
    if (!supportsLarva) {
        return;
    }

    yield call(changeDoorAccessRights);

    const doorAccess = (yield select(isAllowedToManageSelector)) as boolean;
    if (!doorAccess) {
        return;
    }

    const connectionData = (yield select(
        // @ts-ignore
        agreementObjectsSelectors.smartHomeSettingsSelector,
    )) as LarvaConnectionData | null;

    if (!connectionData) {
        return;
    }

    try {
        const webClient = new LarvaWebClient(new LarvaConnectionConfig(connectionData));
        const controller = new LarvaDoorAccessController(webClient);

        // eslint-disable-next-line compat/compat
        const [cards, codes] = (yield Promise.all([
            call(fetchDoorCards, controller),
            call(fetchDoorCodes, controller),
        ])) as [{ [p: string]: DoorCardRawData }, { [p: string]: DoorCodeRawData }];

        if (!cards || !codes) {
            return;
        }
        // @ts-ignore
        yield call(makeDoorAccessDataLists, { cards, codes });
    } catch (error) {
        yield put(coreActions.device.error.notification('Failed to fetch access data or lists', error));
    }
}

export function* makeDoorAccessDataLists({
    cards,
    codes,
}: {
    cards: { [p: string]: DoorCardRawData };
    codes: { [p: string]: DoorCodeRawData };
}): Generator {
    const accessData = (yield call(generateDoorAccessMapData, {
        cards,
        codes,
    })) as { list: IListViewData[]; doorAccessMap: { [accessId: string]: ILarvaDoorAccess } };

    yield put(larvaDoorAccessActions.storeDoorAccessListViewData(accessData.list));
    yield put(larvaDoorAccessActions.storeDoorAccessMapData(accessData.doorAccessMap));
}

export function* fetchDoorCards(controller: LarvaDoorAccessController): Generator {
    const cards: { [cardId: string]: DoorCardRawData } = {};
    let cardsData: IDoorCardRawData[] = [];

    try {
        cardsData = (yield apply(controller, controller.getCards, [])) as IDoorCardRawData[];
    } catch (reason) {
        const response = (reason as AxiosError)?.response;
        yield call(handleErrorResponse, response as AxiosResponse);
        yield put(coreActions.device.error.notification('Failed to fetch cards', reason));
        return;
    }

    for (const data of cardsData as IDoorCardRawData[]) {
        const card = new DoorCardRawData(data);
        cards[card.id] = card;
    }

    yield put(larvaDoorAccessActions.storeDoorCards(cards));
    return cards;
}

export function* fetchDoorCodes(controller: LarvaDoorAccessController): Generator {
    const codes: { [codeId: string]: DoorCodeRawData } = {};
    let codesRawData: IDoorCodeRawData[] = [];

    try {
        codesRawData = (yield apply(controller, controller.getCodes, [])) as IDoorCodeRawData[];
    } catch (reason) {
        yield put(coreActions.device.error.notification(`Failed to fetch codes`, reason));
        const response = (reason as AxiosError)?.response;
        yield call(handleErrorResponse, response as AxiosResponse);
        return;
    }

    for (const data of codesRawData as IDoorCodeRawData[]) {
        const code = new DoorCodeRawData(data);
        codes[code.id] = code;
    }

    yield put(larvaDoorAccessActions.storeDoorCodes(codes));
    return codes;
}
